import { Tournament, TournamentStatus, TournamentType } from '@/src/domain/models';
import { useEffect, useMemo, useState } from 'react';

export type SortOption = 'date-desc' | 'date-asc' | 'name-asc' | 'name-desc';

export interface UseTournamentFiltersReturn {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  filterStatus: TournamentStatus | 'all';
  setFilterStatus: (value: TournamentStatus | 'all') => void;
  filterType: TournamentType | 'all';
  setFilterType: (value: TournamentType | 'all') => void;
  sortBy: SortOption;
  setSortBy: (value: SortOption) => void;
  filteredTournaments: Tournament[];
  hasActiveFilters: boolean;
  clearFilters: () => void;
}

export const useTournamentFilters = (tournaments: Tournament[]): UseTournamentFiltersReturn => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<TournamentStatus | 'all'>('all');
  const [filterType, setFilterType] = useState<TournamentType | 'all'>('all');
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');

  const filteredTournaments = useMemo(() => {
    let filtered = [...tournaments];

    // Search by name or description
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) => t.name.toLowerCase().includes(query) || t.description?.toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter((t) => t.status === filterStatus);
    }

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter((t) => t.tournamentType === filterType);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'date-asc':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });

    return filtered;
  }, [tournaments, searchQuery, filterStatus, filterType, sortBy]);

  const hasActiveFilters =
    searchQuery.trim() !== '' || filterStatus !== 'all' || filterType !== 'all' || sortBy !== 'date-desc';

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
    setFilterType('all');
    setSortBy('date-desc');
  };

  return {
    searchQuery,
    setSearchQuery,
    filterStatus,
    setFilterStatus,
    filterType,
    setFilterType,
    sortBy,
    setSortBy,
    filteredTournaments,
    hasActiveFilters,
    clearFilters,
  };
};
